import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { type RegisterFormValues, registerSchema } from '../validations';
import { useRegisterMutation } from '../queries';
import { useSignupTracking } from './useSignupTracking';
import { useRegistrationAnalytics } from './useRegistrationAnalytics';
import { usePostRegistrationRouting } from './usePostRegistrationRouting';
import { useUrlPrefill } from './useUrlPrefill';
import { useAuthStore } from '@/stores/authStore';
import { handleApiError } from '@/lib/utils/apiErrorHandler';
import { fetchUser } from '@/lib/api/user';
import type { RegisterPayload } from '@/lib/api/auth/types';

export function useRegisterForm() {
    const { mutateAsync: register, isPending } = useRegisterMutation();
    const saveToken = useAuthStore(state => state.saveToken);
    const { trackingData } = useSignupTracking();
    const { trackSignupSuccess, trackSignupFailure } = useRegistrationAnalytics();
    const { navigateAfterRegistration } = usePostRegistrationRouting();

    // Get URL pre-fill values for marketing campaigns and integrations
    const prefill = useUrlPrefill();

    const form = useForm<RegisterFormValues>({
        resolver: zodResolver(registerSchema),
        defaultValues: {
            username: prefill.username ?? '',
            fullName: prefill.fullName ?? '',
            email: prefill.email ?? '',
            phoneNumber: prefill.phoneNumber ?? '',
            countryCode: '',
            password: prefill.password ?? '',
        },
        mode: 'onChange',
    });

    const onSubmit = form.handleSubmit(async data => {
        try {
            // Get timezone
            const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

            // Capture all URL query parameters as experiments
            // Matches Vue's RegisterForm.vue: experiments: this.$route.query
            const searchParams = new URLSearchParams(window.location.search);
            const experiments: Record<string, string> = {};
            searchParams.forEach((value, key) => {
                experiments[key] = value;
            });

            // Build registration payload with UTM and referral tracking
            // Matches Vue's RegisterForm.vue payload structure
            const payload: RegisterPayload = {
                username: data.username.trim(),
                full_name: data.fullName.trim(),
                email: data.email.trim(),
                phone_number: data.phoneNumber,
                password: data.password,
                timezone,
                ...(data.countryCode && { country_code: data.countryCode }),
                // UTM and referral tracking (from useSignupTracking hook)
                ...(trackingData.utm_source && { utm_source: trackingData.utm_source }),
                ...(trackingData.utm_medium && { utm_medium: trackingData.utm_medium }),
                ...(trackingData.utm_campaign && { utm_campaign: trackingData.utm_campaign }),
                ...(trackingData.ref_user && { ref_user: trackingData.ref_user }),
                ...(trackingData.discount_code && { discount_code: trackingData.discount_code }),
                ...(trackingData.explicit_trial_days && { explicit_trial_days: trackingData.explicit_trial_days }),
                // All URL query parameters as experiments (matches Vue's experiments: this.$route.query)
                ...(Object.keys(experiments).length > 0 && { experiments }),
            };

            // Call registration API
            const response = await register(payload);

            // Save token to auth store
            if (response.token) {
                saveToken(response.token);

                // Fetch user data for analytics and routing
                let user = null;
                try {
                    user = await fetchUser(response.token);
                    trackSignupSuccess({
                        userId: user.user_id,
                        username: user.username,
                        email: data.email.trim(),
                        fullName: data.fullName.trim(),
                        utmSource: trackingData.utm_source,
                        utmMedium: trackingData.utm_medium,
                        utmCampaign: trackingData.utm_campaign,
                    });
                } catch (analyticsError) {
                    console.error('Failed to track signup analytics:', analyticsError);
                }

                navigateAfterRegistration(user);
            }
        } catch (error: unknown) {
            // Use centralized error handler - parses error and shows notification
            const result = handleApiError(error, 'Registration Failed');

            // Track failure for analytics
            trackSignupFailure(result.type, result.message);

            // Map API field errors to form fields for validation errors
            if (result.type === 'validation' && result.fieldErrors) {
                const fieldMap: Record<string, keyof RegisterFormValues> = {
                    username: 'username',
                    email: 'email',
                    phone_number: 'phoneNumber',
                    full_name: 'fullName',
                    password: 'password',
                };

                Object.entries(result.fieldErrors).forEach(([field, messages]) => {
                    const formField = fieldMap[field];
                    if (formField && messages[0]) {
                        form.setError(formField, { type: 'server', message: messages[0] });
                    }
                });
            }
        }
    });

    return {
        form,
        onSubmit,
        isSubmitting: isPending,
    };
}
